import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  createChatRead,
  deleteChatReaction,
  getChatMessages,
  readAllChatMessages,
} from "../../../api/api";
import { getWebSocketUrl } from "../../../config";
import { useWebSocket } from "../../../hooks/useWebSocket";
import ChatHeader from "./ChatHeader";
import MessageList from "./MessageList";
import TextEntry from "./TextEntry";
import TypingIndicator from "./TypingIndicator";
import { getSharedMessagePreview } from "../chatListUtils";
import {
  collectImageFiles,
  isFileDragEvent,
} from "../../../lib/contentImages";
import { applyPresenceToChat, recomputeActiveNowCount } from "./presenceUtils";
import { useUserRealtime } from "../../../context/UserRealtimeContext";

const TYPING_PEER_TIMEOUT_MS = 5000;

/**
 * True for a local send that has not been replaced by the server copy.
 * @param {Object|null|undefined} message
 * @returns {boolean}
 */
function isOptimisticMessage(message) {
  return Boolean(message?.client_pending);
}

/**
 * True when a server message is the confirmed copy of a local send.
 * @param {Object|null|undefined} pending
 * @param {Object|null|undefined} confirmed
 * @returns {boolean}
 */
function sameOutgoingMessage(pending, confirmed) {
  if (!isOptimisticMessage(pending) || !confirmed || confirmed.client_pending) {
    return false;
  }
  if (Number(pending.sender_id) !== Number(confirmed.sender_id)) return false;
  if ((Number(pending.parent_id) || 0) !== (Number(confirmed.parent_id) || 0)) {
    return false;
  }
  if ((pending.message_type || "text") !== (confirmed.message_type || "text")) {
    return false;
  }
  return (pending.content || "").trim() === (confirmed.content || "").trim();
}

/**
 * Replace the first matching local send with the server message, keeping its list key.
 * @param {Array<Object>} messages
 * @param {Object} confirmed
 * @returns {Array<Object>|null}
 */
function confirmOptimisticMessage(messages, confirmed) {
  const index = (messages || []).findIndex((message) =>
    sameOutgoingMessage(message, confirmed)
  );
  if (index < 0) return null;
  const next = messages.slice();
  next[index] = {
    ...confirmed,
    local_key: messages[index].local_key,
    client_pending: false,
  };
  return next;
}

/**
 * Merge messages by id (ascending by created_at, then id).
 * @param {Array<Object>} existing
 * @param {Array<Object>} incoming
 * @returns {Array<Object>}
 */
function mergeMessagesById(existing, incoming) {
  const byId = new Map();
  for (const msg of existing || []) {
    if (msg?.id != null) byId.set(Number(msg.id), msg);
  }
  for (const msg of incoming || []) {
    if (msg?.id != null) byId.set(Number(msg.id), msg);
  }
  return Array.from(byId.values()).sort((a, b) => {
    const ta = a.created_at || "";
    const tb = b.created_at || "";
    if (ta !== tb) return ta < tb ? -1 : 1;
    return Number(a.id) - Number(b.id);
  });
}

/**
 * Tag aggregated reactions with viewer-specific flags.
 * @param {Array<Object>} reactions
 * @param {number} userId
 * @returns {Array<Object>}
 */
function withViewerReactionFlags(reactions, userId) {
  return (reactions || []).map((group) => {
    const me = (group.users || []).find(
      (u) => Number(u.user_id) === Number(userId)
    );
    return {
      ...group,
      reacted_by_me: Boolean(me),
      my_reaction_id: me?.reaction_id || 0,
    };
  });
}

/**
 * Normalize getChatMessages payload (wrapped page or legacy list).
 * @param {*} data
 * @returns {{ messages: Array<Object>, has_more: boolean, next_cursor: Object|null }}
 */
function unwrapMessagePage(data) {
  if (Array.isArray(data)) {
    return { messages: data, has_more: false, next_cursor: null };
  }
  return {
    messages: Array.isArray(data?.messages) ? data.messages : [],
    has_more: Boolean(data?.has_more),
    next_cursor: data?.next_cursor || null,
  };
}

/**
 * ChatFrame — main chat interface (page + dock).
 * Sends appear in the thread immediately. The HTTP response and WebSocket
 * replace that local row; WS remains the path for others' messages.
 *
 * @param {Object} props
 * @param {number} props.userId
 * @param {Object} [props.user]
 * @param {Object} [props.chat]
 * @param {number} [props.chatId]
 * @param {function} [props.onMessageUpdate]
 * @param {{ onBack?: () => void, onExpand?: () => void, onClose?: () => void }} [props.panelActions]
 * @param {boolean} [props.compactComposer] - Dock: image icon shares the send slot
 * @param {object} [props.orgChannelOptions]
 * @param {string} [props.currentUserPfp]
 * @param {(chat: object) => void} [props.onChatUpdate] - Sync parent chat list/selection after metadata changes
 * @param {(chatId: number) => void} [props.onRemovedFromChat] - Called when the current user is removed
 * @param {(chatId: number) => void} [props.onChatRead] - Clear local unread when this chat is marked read
 * @param {number} [props.focusMessageId] - Scroll to this message after load
 * @param {number} [props.focusKey] - Increment to re-run scroll when the same id is requested again
 * @returns {JSX.Element}
 */
function ChatFrame({
  userId,
  user,
  chat,
  chatId,
  onMessageUpdate,
  panelActions,
  compactComposer = false,
  orgChannelOptions,
  currentUserPfp,
  onChatUpdate,
  onRemovedFromChat,
  onChatRead,
  focusMessageId = null,
  focusKey = 0,
}) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [chatInfo, setChatInfo] = useState(chat || null);
  const [error, setError] = useState("");
  const [typingUsers, setTypingUsers] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const { presenceByUserId, reportPresence } = useUserRealtime();

  const currentChatId = chatId || chat?.id;
  const currentChatIdRef = useRef(currentChatId);
  currentChatIdRef.current = currentChatId;
  const nextCursorRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const onMessageUpdateRef = useRef(onMessageUpdate);
  const onChatUpdateRef = useRef(onChatUpdate);
  const onRemovedFromChatRef = useRef(onRemovedFromChat);
  const onChatReadRef = useRef(onChatRead);
  const typingTimersRef = useRef(new Map());
  const textEntryRef = useRef(null);

  const userName =
    user?.first_name || user?.last_name
      ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
      : user?.username || user?.name || "";

  useEffect(() => {
    return () => {
      currentChatIdRef.current = null;
    };
  }, []);

  useEffect(() => {
    nextCursorRef.current = nextCursor;
  }, [nextCursor]);

  useEffect(() => {
    onMessageUpdateRef.current = onMessageUpdate;
  }, [onMessageUpdate]);

  useEffect(() => {
    onChatUpdateRef.current = onChatUpdate;
  }, [onChatUpdate]);

  useEffect(() => {
    onRemovedFromChatRef.current = onRemovedFromChat;
  }, [onRemovedFromChat]);

  useEffect(() => {
    onChatReadRef.current = onChatRead;
  }, [onChatRead]);

  useEffect(() => {
    for (const member of chatInfo?.other_members || []) {
      if (member?.user_id && member.last_online) {
        reportPresence(member.user_id, member.last_online);
      }
    }
  }, [chatInfo, reportPresence]);

  useEffect(() => {
    return () => {
      typingTimersRef.current.forEach((timer) => clearTimeout(timer));
      typingTimersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    setIsDraggingFiles(false);
  }, [currentChatId]);

  /**
   * Attach dropped image files to the composer.
   * @param {DragEvent} event
   * @returns {void}
   */
  function handleFileDragEnter(event) {
    if (!isFileDragEvent(event)) return;
    event.preventDefault();
    setIsDraggingFiles(true);
  }

  /**
   * @param {DragEvent} event
   * @returns {void}
   */
  function handleFileDragLeave(event) {
    if (!isFileDragEvent(event)) return;
    const related = event.relatedTarget;
    if (related && event.currentTarget.contains(related)) return;
    setIsDraggingFiles(false);
  }

  /**
   * @param {DragEvent} event
   * @returns {void}
   */
  function handleFileDragOver(event) {
    if (!isFileDragEvent(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  }

  /**
   * @param {DragEvent} event
   * @returns {void}
   */
  function handleFileDrop(event) {
    if (!isFileDragEvent(event)) return;
    event.preventDefault();
    setIsDraggingFiles(false);
    const files = collectImageFiles(event.dataTransfer);
    textEntryRef.current?.addImageFiles(files.length ? files : event.dataTransfer?.files);
  }

  /**
   * Mark all unread messages in the open chat as read and clear local unread.
   * @param {number} chatIdNum
   * @returns {Promise<void>}
   */
  const markChatRead = useCallback(async (chatIdNum) => {
    if (!chatIdNum || !userId) return;
    onChatReadRef.current?.(chatIdNum);
    try {
      await readAllChatMessages(chatIdNum, userId);
    } catch (err) {
      console.error("Failed to mark chat as read:", err);
    }
  }, [userId]);

  /**
   * Mark a single inbound message as read while this chat is open.
   * @param {number} chatIdNum
   * @param {number} messageId
   * @returns {Promise<void>}
   */
  const markMessageRead = useCallback(async (chatIdNum, messageId) => {
    if (!chatIdNum || !messageId || !userId) return;
    onChatReadRef.current?.(chatIdNum);
    try {
      await createChatRead({
        message_id: messageId,
        chat_id: chatIdNum,
        user_id: userId,
      });
    } catch (err) {
      console.error("Failed to mark message as read:", err);
    }
  }, [userId]);

  /**
   * Apply a peer's advanced last-read cursor to other_members.
   * @param {number} readerUserId
   * @param {number} lastReadMessageId
   */
  const applyMessagesRead = useCallback(
    (readerUserId, lastReadMessageId) => {
      const peerId = Number(readerUserId);
      const cursor = Number(lastReadMessageId);
      if (!peerId || !Number.isFinite(cursor) || peerId === Number(userId)) {
        return;
      }
      setChatInfo((prev) => {
        if (!prev) return prev;
        const others = prev.other_members || [];
        let changed = false;
        const nextOthers = others.map((m) => {
          if (Number(m.user_id) !== peerId) return m;
          const prevCursor = Number(m.last_read_message_id) || 0;
          if (cursor <= prevCursor) return m;
          changed = true;
          return { ...m, last_read_message_id: cursor };
        });
        if (!changed) return prev;
        const next = { ...prev, other_members: nextOthers };
        if (typeof onChatUpdateRef.current === "function") {
          onChatUpdateRef.current(next);
        }
        return next;
      });
    },
    [userId]
  );

  /**
   * Append a message if its id is not already in the list.
   * @param {Object} msg
   */
  const appendMessage = useCallback((msg) => {
    if (msg?.id == null) return;
    setMessages((prev) => {
      if (!msg.client_pending) {
        const confirmed = confirmOptimisticMessage(prev, msg);
        if (confirmed) return confirmed;
      }
      if (
        msg.local_key &&
        prev.some((m) => m.local_key && m.local_key === msg.local_key)
      ) {
        return prev;
      }
      if (prev.some((m) => Number(m.id) === Number(msg.id))) return prev;
      return [...prev, msg];
    });
  }, []);

  /**
   * Mark a message as soft-deleted in local state (tombstone).
   * @param {number} messageId
   * @param {string} [deletedAt]
   */
  const markMessageDeleted = useCallback((messageId, deletedAt) => {
    const tombstoneAt = deletedAt || new Date().toISOString();
    setReplyTo((prev) =>
      Number(prev?.id) === Number(messageId) ? null : prev
    );
    setMessages((prev) =>
      prev.map((m) => {
        if (Number(m.id) === Number(messageId)) {
          return {
            ...m,
            deleted_at: tombstoneAt,
            reactions: [],
            reaction_count: 0,
            user_reacted: false,
            user_reaction_id: 0,
          };
        }
        if (Number(m.parent_id) === Number(messageId)) {
          return {
            ...m,
            parent_content: "",
            parent_deleted_at: tombstoneAt,
          };
        }
        return m;
      })
    );
  }, []);

  /**
   * Apply aggregated reaction payload to a message.
   * @param {number} messageId
   * @param {Array<Object>} reactions
   * @param {number} reactionCount
   */
  const applyReactionUpdate = useCallback(
    (messageId, reactions, reactionCount) => {
      const tagged = withViewerReactionFlags(reactions, userId);
      const mine = tagged.find((g) => g.reacted_by_me);
      setMessages((prev) =>
        prev.map((m) =>
          Number(m.id) === Number(messageId)
            ? {
                ...m,
                reactions: tagged,
                reaction_count:
                  typeof reactionCount === "number"
                    ? reactionCount
                    : tagged.reduce((sum, g) => sum + (g.count || 0), 0),
                user_reacted: Boolean(mine),
                user_reaction_id: mine?.my_reaction_id || 0,
              }
            : m
        )
      );
    },
    [userId]
  );

  /**
   * Track peer typing with a 5s inactivity clear.
   * @param {{ user_id: number, user_name?: string, is_typing: boolean }} payload
   */
  const handleTypingEvent = useCallback((payload) => {
    const peerId = Number(payload.user_id);
    if (!peerId || peerId === Number(userId)) return;

    const timers = typingTimersRef.current;
    if (timers.has(peerId)) {
      clearTimeout(timers.get(peerId));
      timers.delete(peerId);
    }

    if (!payload.is_typing) {
      setTypingUsers((prev) => prev.filter((u) => Number(u.user_id) !== peerId));
      return;
    }

    const fromPayload = (payload.user_name || "").trim();
    setTypingUsers((prev) => {
      const next = prev.filter((u) => Number(u.user_id) !== peerId);
      let resolvedName = fromPayload;
      if (!resolvedName) {
        const member = (chatInfo?.other_members || []).find(
          (m) => Number(m.user_id) === peerId
        );
        resolvedName =
          (member?.name || member?.username || "").trim() || "Someone";
      }
      next.push({
        user_id: peerId,
        user_name: resolvedName,
      });
      return next;
    });

    timers.set(
      peerId,
      setTimeout(() => {
        timers.delete(peerId);
        setTypingUsers((prev) =>
          prev.filter((u) => Number(u.user_id) !== peerId)
        );
      }, TYPING_PEER_TIMEOUT_MS)
    );
  }, [userId, chatInfo?.other_members]);

  const handleIncomingMessage = useCallback(
    (data) => {
      if (!data?.type) return;
      const activeChatId = currentChatIdRef.current;

      if (data.type === "new_message" && data.data) {
        const newMessage = data.data;
        if (
          newMessage.chat_id != null &&
          activeChatId != null &&
          Number(newMessage.chat_id) !== Number(activeChatId)
        ) {
          return;
        }
        appendMessage({
          ...newMessage,
          reactions: withViewerReactionFlags(newMessage.reactions, userId),
          is_own_message: Number(newMessage.sender_id) === Number(userId),
        });
        if (
          onMessageUpdateRef.current &&
          newMessage.content &&
          !newMessage.deleted_at
        ) {
          onMessageUpdateRef.current(
            activeChatId,
            newMessage.content,
            newMessage.created_at
          );
        }
        if (
          newMessage.id != null &&
          Number(newMessage.sender_id) !== Number(userId) &&
          !newMessage.deleted_at
        ) {
          markMessageRead(activeChatId, Number(newMessage.id));
        }
        return;
      }

      if (data.type === "message_deleted" && data.data?.id != null) {
        markMessageDeleted(data.data.id, data.data.deleted_at);
        return;
      }

      if (data.type === "reaction_updated" && data.message_id != null) {
        applyReactionUpdate(
          data.message_id,
          data.reactions || [],
          data.reaction_count
        );
        return;
      }

      if (data.type === "messages_read") {
        applyMessagesRead(data.user_id, data.last_read_message_id);
        return;
      }

      if (data.type === "typing") {
        handleTypingEvent(data);
        return;
      }

      if (data.type === "member_joined" && data.member) {
        const member = data.member;
        const memberUserId = Number(member.user_id);
        setChatInfo((prev) => {
          if (!prev) return prev;
          const others = prev.other_members || [];
          let nextOthers = others;
          if (
            memberUserId !== Number(userId) &&
            !others.some((m) => Number(m.user_id) === memberUserId)
          ) {
            nextOthers = [
              ...others,
              {
                ...member,
                user_id: memberUserId,
                name: member.name || member.username || "User",
                pfp: member.pfp || member.pfp_url || "",
              },
            ];
          }
          const next = {
            ...prev,
            other_members: nextOthers,
            member_count: Math.max(
              (prev.member_count || others.length + 1) + 1,
              nextOthers.length + 1
            ),
          };
          next.active_now_count = recomputeActiveNowCount(next);
          if (typeof onChatUpdateRef.current === "function") {
            onChatUpdateRef.current(next);
          }
          return next;
        });
        return;
      }

      if (data.type === "member_left") {
        const leftUserId = Number(data.user_id ?? data.member?.user_id);
        if (leftUserId && leftUserId === Number(userId)) {
          if (typeof onRemovedFromChatRef.current === "function") {
            onRemovedFromChatRef.current(activeChatId);
          }
          return;
        }
        setChatInfo((prev) => {
          if (!prev) return prev;
          const others = (prev.other_members || []).filter(
            (m) => Number(m.user_id) !== leftUserId
          );
          const next = {
            ...prev,
            other_members: others,
            member_count: Math.max(1, (prev.member_count || 2) - 1),
          };
          next.active_now_count = recomputeActiveNowCount(next);
          if (typeof onChatUpdateRef.current === "function") {
            onChatUpdateRef.current(next);
          }
          return next;
        });
      }

      if (data.type === "presence") {
        const presenceUserId = Number(data.user_id);
        const lastOnline = data.last_online || "";
        if (!presenceUserId) return;
        reportPresence(presenceUserId, lastOnline);
        setChatInfo((prev) => {
          if (!prev) return prev;
          const next = applyPresenceToChat(
            prev,
            presenceUserId,
            lastOnline,
            new Date(),
            presenceByUserId
          );
          if (next === prev) return prev;
          if (typeof onChatUpdateRef.current === "function") {
            onChatUpdateRef.current(next);
          }
          return next;
        });
      }
    },
    [
      appendMessage,
      applyMessagesRead,
      applyReactionUpdate,
      handleTypingEvent,
      markMessageDeleted,
      markMessageRead,
      userId,
      reportPresence,
      presenceByUserId,
    ]
  );

  const wsUrl =
    currentChatId && userId
      ? getWebSocketUrl(`/ws/chat/${currentChatId}/?user_id=${userId}`)
      : null;
  const { send: sendWs } = useWebSocket(wsUrl, handleIncomingMessage);

  const handleTypingChange = useCallback(
    (isTyping) => {
      sendWs({
        type: "typing",
        is_typing: Boolean(isTyping),
        user_name: userName,
      });
    },
    [sendWs, userName]
  );

  const handleRemoveOwnReaction = useCallback(
    async (reactionId) => {
      if (!reactionId) return;
      try {
        await deleteChatReaction(reactionId);
      } catch (err) {
        console.error("Failed to remove reaction:", err);
      }
    },
    []
  );

  /**
   * Whether an in-flight request still belongs to the open chat.
   * @param {number} requestedChatId
   * @returns {boolean}
   */
  const isCurrentChat = useCallback((requestedChatId) => {
    return Number(currentChatIdRef.current) === Number(requestedChatId);
  }, []);

  const fetchLatestMessages = useCallback(async () => {
    if (!currentChatId) return;

    const requestedChatId = Number(currentChatId);
    const aroundId =
      focusMessageId != null && Number(focusMessageId) > 0
        ? Number(focusMessageId)
        : null;

    try {
      setLoading(true);
      setError("");
      const res = await getChatMessages(requestedChatId, userId, {
        limit: 30,
        ...(aroundId ? { aroundId } : {}),
      });
      if (!isCurrentChat(requestedChatId)) return;
      let payload = res;
      if (aroundId && (res.success === false || res.data == null)) {
        payload = await getChatMessages(requestedChatId, userId, {
          limit: 30,
        });
        if (!isCurrentChat(requestedChatId)) return;
      }
      if (payload.success && payload.data != null) {
        const page = unwrapMessagePage(payload.data);
        setHasMore(page.has_more);
        setNextCursor(page.next_cursor);
        setMessages(page.messages);
      } else {
        setError(payload.error || "Failed to load messages");
        setHasMore(false);
        setNextCursor(null);
      }
    } catch (err) {
      if (!isCurrentChat(requestedChatId)) return;
      setError("Network error loading messages");
      setHasMore(false);
      setNextCursor(null);
    } finally {
      if (isCurrentChat(requestedChatId)) {
        setLoading(false);
      }
    }
  }, [currentChatId, userId, focusMessageId, focusKey, isCurrentChat]);

  const fetchOlderMessages = useCallback(async () => {
    if (!currentChatId || !nextCursorRef.current || loadingMoreRef.current) {
      return;
    }

    const requestedChatId = Number(currentChatId);
    const cursor = nextCursorRef.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const res = await getChatMessages(requestedChatId, userId, {
        limit: 20,
        beforeCreatedAt: cursor.created_at,
        beforeId: cursor.id,
      });
      if (!isCurrentChat(requestedChatId)) return;
      if (res.success && res.data != null) {
        const page = unwrapMessagePage(res.data);
        setHasMore(page.has_more);
        setNextCursor(page.next_cursor);
        setMessages((prev) => mergeMessagesById(prev, page.messages));
      }
    } catch {
      // Keep current messages; user can retry by scrolling again.
    } finally {
      loadingMoreRef.current = false;
      if (isCurrentChat(requestedChatId)) {
        setLoadingMore(false);
      }
    }
  }, [currentChatId, userId, isCurrentChat]);

  useEffect(() => {
    setChatInfo(chat || null);
  }, [chat]);

  /**
   * Update local chat header state and notify parent pages/dock.
   * @param {object} updated
   * @returns {void}
   */
  const handleChatInfoUpdate = useCallback(
    (updated) => {
      if (!updated) return;
      setChatInfo(updated);
      if (typeof onChatUpdate === "function") {
        onChatUpdate(updated);
      }
    },
    [onChatUpdate]
  );

  useEffect(() => {
    if (currentChatId) {
      setMessages([]);
      setError("");
      setHasMore(false);
      setNextCursor(null);
      setLoadingMore(false);
      loadingMoreRef.current = false;
      setTypingUsers([]);
      setReplyTo(null);
      typingTimersRef.current.forEach((timer) => clearTimeout(timer));
      typingTimersRef.current.clear();
      fetchLatestMessages();
      markChatRead(parseInt(currentChatId, 10));
    } else {
      setMessages([]);
      setLoading(false);
      setLoadingMore(false);
      setHasMore(false);
      setNextCursor(null);
      setError("");
      setTypingUsers([]);
      setReplyTo(null);
    }
  }, [currentChatId, fetchLatestMessages, markChatRead]);

  // Fallback poll: pick up messages if the WebSocket drops (e.g. CRA /ws conflict).
  // Never prepend older history here — that shifts scrollTop; pagination owns that.
  useEffect(() => {
    if (!currentChatId || !userId) return undefined;

    /**
     * @param {Array<Object>} prev
     * @param {Array<Object>} incoming
     * @returns {{ messages: Array<Object>, appended: Array<Object> }}
     */
    function mergePollMessages(prev, incoming) {
      let working = prev;
      for (const msg of incoming || []) {
        const settled = confirmOptimisticMessage(working, msg);
        if (settled) working = settled;
      }

      if (!working.length) {
        return {
          messages: mergeMessagesById([], incoming),
          // Open-chat markChatRead owns the empty→loaded case; avoid N createChatRead calls.
          appended: [],
        };
      }

      const incomingById = new Map(
        (incoming || [])
          .filter((m) => m?.id != null)
          .map((m) => [Number(m.id), m])
      );
      const newestConfirmed = [...working]
        .reverse()
        .find((message) => !isOptimisticMessage(message));
      const newestTime = newestConfirmed?.created_at || "";
      const newestId = Number(newestConfirmed?.id);

      let changed = working !== prev;
      const next = working.map((m) => {
        const incomingMsg = incomingById.get(Number(m.id));
        if (!incomingMsg) return m;
        if (
          m.deleted_at !== incomingMsg.deleted_at ||
          m.reaction_count !== incomingMsg.reaction_count ||
          JSON.stringify(m.reactions) !== JSON.stringify(incomingMsg.reactions)
        ) {
          changed = true;
          return { ...m, ...incomingMsg, local_key: m.local_key };
        }
        return m;
      });

      const knownIds = new Set(next.map((m) => Number(m.id)));
      const viewingLiveTail = !newestConfirmed || incomingById.has(newestId);
      if (!viewingLiveTail) {
        // Around-message / history windows are not the live tail. Do not glue
        // the latest page onto a gap — that changes newest id and yanks scroll.
        return { messages: changed ? next : prev, appended: [] };
      }

      const toAppend = [];
      for (const msg of incoming || []) {
        if (msg?.id == null) continue;
        const id = Number(msg.id);
        if (knownIds.has(id)) continue;
        const t = msg.created_at || "";
        const isNewer =
          t > newestTime || (t === newestTime && id > newestId);
        if (isNewer) {
          toAppend.push(msg);
        }
      }

      if (toAppend.length === 0) {
        return { messages: changed ? next : prev, appended: [] };
      }

      const merged = mergeMessagesById(next, toAppend);
      const newestMerged = merged[merged.length - 1];
      if (
        newestMerged?.content &&
        !newestMerged.deleted_at &&
        onMessageUpdateRef.current &&
        Number(newestMerged.id) !== newestId
      ) {
        onMessageUpdateRef.current(
          currentChatId,
          newestMerged.content,
          newestMerged.created_at
        );
      }
      return { messages: merged, appended: toAppend };
    }

    const requestedChatId = Number(currentChatId);
    const poll = async () => {
      try {
        const res = await getChatMessages(requestedChatId, userId, {
          limit: 30,
        });
        if (Number(currentChatIdRef.current) !== requestedChatId) return;
        if (res.success === false || res.data == null) return;
        const page = unwrapMessagePage(res.data);
        if (page.messages.length === 0) return;
        let appended = [];
        setMessages((prev) => {
          const result = mergePollMessages(prev, page.messages);
          appended = result.appended;
          return result.messages;
        });
        if (Number(currentChatIdRef.current) !== requestedChatId) return;
        for (const msg of appended) {
          if (
            msg?.id != null &&
            Number(msg.sender_id) !== Number(userId) &&
            !msg.deleted_at
          ) {
            markMessageRead(requestedChatId, Number(msg.id));
          }
        }
      } catch {
        // ignore transient poll errors
      }
    };

    const id = setInterval(poll, 4000);
    return () => clearInterval(id);
  }, [currentChatId, markMessageRead, userId]);

  /**
   * Show a local send immediately and refresh the sidebar preview.
   * @param {Object} draft
   */
  const handleOptimisticSend = useCallback(
    (draft) => {
      if (!draft || draft.tempId == null) return;
      const reply = draft.replyTo;
      const message = {
        id: draft.tempId,
        local_key: `local-${draft.tempId}`,
        client_pending: true,
        chat_id: currentChatId,
        sender_id: userId,
        sender_name: userName,
        sender_pfp: currentUserPfp || user?.pfp_url || "",
        content: draft.content || "",
        message_type: draft.messageType || "text",
        metadata: draft.metadata || "",
        mentions: draft.mentions || [],
        created_at: new Date().toISOString(),
        parent_id: reply?.id || 0,
        parent_content: reply ? getSharedMessagePreview(reply) : "",
        parent_sender_name: reply?.sender_name || "",
        parent_sender_id: reply?.sender_id,
        parent_message_type: reply?.message_type || "text",
        parent_metadata: reply?.metadata || "",
        parent_deleted_at: reply?.deleted_at || "",
        reactions: [],
        reaction_count: 0,
        is_own_message: true,
        deleted_at: "",
      };
      setReplyTo(null);
      appendMessage(message);
      if (onMessageUpdateRef.current) {
        const preview = getSharedMessagePreview(message);
        if (preview) {
          onMessageUpdateRef.current(
            currentChatId,
            preview,
            message.created_at
          );
        }
      }
    },
    [appendMessage, currentChatId, currentUserPfp, user?.pfp_url, userId, userName]
  );

  /**
   * Drop a local send that the server rejected.
   * @param {number} tempId
   */
  const handleOptimisticFailed = useCallback((tempId) => {
    setMessages((prev) => prev.filter((message) => message.id !== tempId));
  }, []);

  /**
   * After a successful send: replace the local row, update sidebar preview.
   * @param {Object} message
   */
  const handleMessageSent = useCallback(
    (message) => {
      if (!message) return;
      setReplyTo(null);
      appendMessage(message);
      if (onMessageUpdateRef.current) {
        const preview = getSharedMessagePreview(message);
        if (preview) {
          onMessageUpdateRef.current(
            currentChatId,
            preview,
            message.created_at || message.timestamp
          );
        }
      }
    },
    [appendMessage, currentChatId]
  );

  /**
   * Start composing a reply to a message.
   * @param {Object} message
   * @returns {void}
   */
  const handleReply = useCallback((message) => {
    if (!message?.id || message.deleted_at) return;
    setReplyTo(message);
  }, []);

  if (!currentChatId) {
    return (
      <div className="flex flex-1 items-center justify-center bg-primary">
        <div className="text-center text-text-muted">
          <h3 className="mb-2 text-lg font-medium text-text-secondary">
            Select a chat
          </h3>
          <p className="text-text-muted">
            Choose a conversation from the list to start messaging
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative flex h-full min-h-0 flex-col overflow-x-hidden bg-primary"
      onDragEnter={handleFileDragEnter}
      onDragLeave={handleFileDragLeave}
      onDragOver={handleFileDragOver}
      onDrop={handleFileDrop}
    >
      {isDraggingFiles ? (
        <div
          className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center border-2 border-dashed border-accent bg-ui-overlay"
          aria-hidden
        >
          <span className="rounded-md bg-ui-card px-3 py-2 text-sm text-text-primary shadow-sm">
            Drop photos to attach
          </span>
        </div>
      ) : null}
      <ChatHeader
        chatId={parseInt(currentChatId, 10)}
        chatInfo={chatInfo}
        userId={userId}
        onChatInfoUpdate={handleChatInfoUpdate}
        panelActions={panelActions}
        orgChannelOptions={orgChannelOptions}
        currentUserPfp={currentUserPfp}
      />

      <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden">
        <MessageList
          chat={chatInfo}
          chatId={parseInt(currentChatId, 10)}
          messages={messages}
          loading={loading}
          loadingMore={loadingMore}
          error={error}
          userId={userId}
          onLoadMore={fetchOlderMessages}
          hasMore={hasMore}
          onRetry={fetchLatestMessages}
          onMessageDeleted={markMessageDeleted}
          onRemoveOwnReaction={handleRemoveOwnReaction}
          onReply={handleReply}
          focusMessageId={focusMessageId}
          focusKey={focusKey}
        />

        <TypingIndicator typingUsers={typingUsers} />

        <TextEntry
          ref={textEntryRef}
          chatId={parseInt(currentChatId, 10)}
          userId={userId}
          onOptimisticSend={handleOptimisticSend}
          onOptimisticFailed={handleOptimisticFailed}
          onMessageSent={handleMessageSent}
          onTypingChange={handleTypingChange}
          mentionCandidates={chatInfo?.other_members || []}
          includeAll={
            chatInfo?.chat_type === "group" || Boolean(chatInfo?.org_id)
          }
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          compact={compactComposer}
        />
      </div>
    </div>
  );
}

export default ChatFrame;
